import { normalizeFormOfficeForApi, type FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";

const readNestedString = (row: Record<string, unknown>, path: string[]): string => {
  let current: unknown = row;
  for (const key of path) {
    if (current == null || typeof current !== "object") return "";
    current = (current as Record<string, unknown>)[key];
  }
  return current == null ? "" : String(current).trim();
};

/** Read `form_office` from root or nested applicant/user info shapes. */
export const readRowFormOffice = (row: unknown): string => {
  if (row == null || typeof row !== "object") return "";
  const record = row as Record<string, unknown>;

  const candidates = [
    readNestedString(record, ["form_office"]),
    readNestedString(record, ["formOffice"]),
    readNestedString(record, ["office"]),
    readNestedString(record, ["campus"]),
    readNestedString(record, ["applicant_info", "form_office"]),
    readNestedString(record, ["applicantInfo", "form_office"]),
    readNestedString(record, ["user_info", "form_office"]),
    readNestedString(record, ["userInfo", "form_office"]),
  ];

  return candidates.find((value) => value.trim()) ?? "";
};

/** Read `office_url` from root or nested applicant/user info shapes. */
export const readRowOfficeUrl = (row: unknown): string => {
  if (row == null || typeof row !== "object") return "";
  const record = row as Record<string, unknown>;

  const candidates = [
    readNestedString(record, ["office_url"]),
    readNestedString(record, ["officeUrl"]),
    readNestedString(record, ["applicant_info", "office_url"]),
    readNestedString(record, ["applicantInfo", "office_url"]),
    readNestedString(record, ["user_info", "office_url"]),
    readNestedString(record, ["userInfo", "office_url"]),
  ];

  return candidates.find((value) => value.trim()) ?? "";
};

/**
 * Point 1 — site / office_url access.
 * - no site selected → keep everything
 * - empty/missing office_url → only visible when no site is selected
 * - specific site selected → exact match with row office_url
 */
export const matchesSiteAccess = (officeUrl: string | null | undefined, selectedSiteUrl: string): boolean => {
  const selected = String(selectedSiteUrl ?? "").trim();
  if (!selected) return true;

  const url = String(officeUrl ?? "").trim();
  if (!url) return false;

  return url === selected;
};

/**
 * Point 3 — country / form_office access.
 * Full-access roles keep every row; locked roles keep only matching form_office.
 */
export const matchesCountryAccess = (
  formOffice: string | null | undefined,
  allowedOffices: FormOfficeApi[],
  hasFullCountryAccess: boolean,
): boolean => {
  if (hasFullCountryAccess) return true;
  const normalized = normalizeFormOfficeForApi(formOffice);
  if (!normalized) return false;
  return allowedOffices.includes(normalized);
};

export interface AccessFilterOptions {
  selectedSiteUrl: string;
  allowedOffices: FormOfficeApi[];
  hasFullCountryAccess: boolean;
}

/** Apply site + country access filters to table rows. */
export const filterRowsByAccess = <T,>(rows: T[], options: AccessFilterOptions): T[] =>
  rows.filter((row) => {
    const officeUrl = readRowOfficeUrl(row);
    const formOffice = readRowFormOffice(row);
    return (
      matchesSiteAccess(officeUrl, options.selectedSiteUrl) &&
      matchesCountryAccess(formOffice, options.allowedOffices, options.hasFullCountryAccess)
    );
  });
